import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import type { Rule } from "antd/es/form";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { LabeledValue } from "antd/es/select";
import Tooltip from "antd/es/tooltip";
import type { CustomTagProps, DisplayValueType } from "rc-select/lib/BaseSelect";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import { Person } from "@mapmycustomers/shared/types/entity";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { primaryEntityComparator } from "@mapmycustomers/shared/util/comparator";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { EntityTypeShapedIcon, FormItem, SelectField } from "@mapmycustomers/ui";

import PeopleAssociation from "@app/component/associations/PeopleAssociation";
import EntityChip from "@app/component/EntityChip";
import PeopleOverflowTooltip from "@app/component/PeopleOverflowTooltip";
import { fetchPeople, fetchPerson } from "@app/store/person/actions";
import analyticsService from "@app/util/analytic/AnalyticsService";
import showRelateEntitiesAlert from "@app/util/form/showRelateEntitiesAlert";

import styles from "./CommonRelationshipField.module.scss";
import fetchPeopleByIds from "./fetchPeopleByIds";
import { MULTI_PERSON_LIMIT } from "./personRelationshipFieldMessages";
import { PersonRelationshipFieldProps } from "./PersonRelationshipFieldProps";
import resolveSelectedPeople from "./resolveSelectedPeople";

export { MULTI_PERSON_LIMIT };

const PersonRelationshipField = ({
  allowAdd,
  allowClear,
  associatedCompany,
  associatedDeal,
  associatedPeople,
  className,
  disabled,
  extraRules,
  hideRelateEntitiesAlert,
  label,
  maxTagCount = "responsive",
  multiselect = false,
  onChangeAssociatedEntities,
  onFetchPeople,
  onFetchPerson,
  relateEntities,
  required,
  suggestedPeople,
  zIndex,
}: PersonRelationshipFieldProps) => {
  const form = useFormInstance();
  const intl = useIntl();

  const [modalVisible, showModal, hideModal] = useBoolean();
  const [selectedPeople, setSelectedPeople] = useState<Person[]>(() =>
    resolveSelectedPeople(multiselect, associatedPeople)
  );
  // "Set as Primary" pick on a not-yet-committed row; applied at handleSelect.
  const [pendingPrimaryId, setPendingPrimaryId] = useState<Person["id"] | undefined>();

  const notifyAssociations = useCallback(
    (people: Person[], runRelateAlert: boolean) => {
      const assignee = form.getFieldValue("assignee");
      const primaryPerson = people[0];
      onChangeAssociatedEntities?.({
        assignee,
        company:
          associatedCompany ??
          primaryPerson?.primaryAccount ??
          primaryPerson?.accounts?.[0] ??
          undefined,
        deal: associatedDeal,
        people,
        person: primaryPerson,
      });
      if (runRelateAlert && primaryPerson) {
        showRelateEntitiesAlert(
          intl,
          assignee,
          associatedCompany,
          primaryPerson,
          associatedDeal,
          relateEntities
        );
      }
    },
    [associatedCompany, associatedDeal, form, intl, onChangeAssociatedEntities, relateEntities]
  );

  // Multi mode binds to peopleIds (array); single mode to personId (primitive).
  const formFieldName = multiselect ? "peopleIds" : "personId";

  const commitSelectedPeople = useCallback(
    (people: Person[], runRelateAlert = false) => {
      setSelectedPeople(people);
      const ids = people.map((person) => person.id);
      if (multiselect) {
        form.setFieldValue("peopleIds", ids);
      } else {
        form.setFieldValue("personId", ids[0]);
      }
      notifyAssociations(people, runRelateAlert);
    },
    [form, multiselect, notifyAssociations]
  );

  const handleSelect = useCallback(
    // PeopleAssociation reports deltas; compose the full list off selectedPeople.
    async (newlyAddedIds: Person["id"][], removedIds: Person["id"][] = []) => {
      if (multiselect) {
        const removedSet = new Set(removedIds);
        const existingById = new Map(selectedPeople.map((person) => [person.id, person]));
        const keptIds = selectedPeople
          .map((person) => person.id)
          .filter((id) => !removedSet.has(id));
        const finalIds = Array.from(new Set([...keptIds, ...newlyAddedIds]));
        analyticsService.completed("People Selection", { peopleCount: finalIds.length });
        if (!finalIds.length) {
          commitSelectedPeople([]);
          return;
        }
        const idsToFetch = newlyAddedIds.filter((id) => !existingById.has(id));
        const fetched = idsToFetch.length ? await fetchPeopleByIds(idsToFetch, onFetchPeople) : [];
        const fetchedById = new Map(fetched.map((person) => [person.id, person]));
        const ordered = finalIds
          .map((id) => existingById.get(id) ?? fetchedById.get(id))
          .filter(isDefined);
        const finalOrdered =
          pendingPrimaryId !== undefined && ordered.some((person) => person.id === pendingPrimaryId)
            ? ordered.toSorted(primaryEntityComparator(pendingPrimaryId))
            : ordered;
        setPendingPrimaryId(undefined);
        commitSelectedPeople(finalOrdered, !hideRelateEntitiesAlert);
        return;
      }

      const personId = newlyAddedIds[0];
      if (personId === undefined) {
        commitSelectedPeople([]);
        return;
      }
      onFetchPerson({
        id: personId,
        callback: (person: Person) => {
          commitSelectedPeople(person ? [person] : [], !hideRelateEntitiesAlert);
        },
        options: { includeAccessStatus: true, includeUsersWithAccess: true },
      });
    },
    [
      commitSelectedPeople,
      hideRelateEntitiesAlert,
      multiselect,
      onFetchPeople,
      onFetchPerson,
      pendingPrimaryId,
      selectedPeople,
    ]
  );

  const handleRemove = useCallback(
    (removedId?: Person["id"]) => {
      if (multiselect && removedId !== undefined) {
        const remaining = selectedPeople.filter((person) => person.id !== removedId);
        analyticsService.clicked(["Activity Create Modal", "Remove Person"], {
          peopleCountAfter: remaining.length,
        });
        commitSelectedPeople(remaining);
        return;
      }
      commitSelectedPeople([]);
    },
    [commitSelectedPeople, multiselect, selectedPeople]
  );

  const handleOpenModal = useCallback(() => {
    if (multiselect) {
      analyticsService.clicked(["Activity Create Modal", "Add People"]);
    }
    showModal();
  }, [multiselect, showModal]);

  // pendingPrimaryId surfaces the picker's pending pick; ignored once modal closes if the row is gone.
  const primaryPersonId =
    pendingPrimaryId !== undefined &&
    (modalVisible || selectedPeople.some((person) => person.id === pendingPrimaryId))
      ? pendingPrimaryId
      : selectedPeople[0]?.id;

  // Defer to handleSelect; eager commit caused double dispatch on cancel-revert.
  const handleUpdatePrimaryPerson = useCallback((newPrimaryId?: Person["id"]) => {
    setPendingPrimaryId(newPrimaryId);
  }, []);

  const options = useMemo<LabeledValue[]>(
    () =>
      selectedPeople.map((person) => ({
        key: String(person.id),
        label: person.name,
        value: person.id,
      })),
    [selectedPeople]
  );

  const tagRender = useCallback(
    ({ label, value }: CustomTagProps) => (
      <Tooltip
        title={
          primaryPersonId === value
            ? intl.formatMessage({
                id: "formFields.personRelationshipField.primary",
                defaultMessage: "Primary Person",
                description: "Tooltip on the primary person chip",
              })
            : null
        }
      >
        <EntityChip
          entityId={value as Person["id"]}
          entityType={EntityType.PERSON}
          name={label as string}
          primary={primaryPersonId === value}
          tooltipProps={{ trigger: [] }}
        />
      </Tooltip>
    ),
    [intl, primaryPersonId]
  );

  const maxTagPlaceholder = useCallback(
    (omittedValues: DisplayValueType[]) => (
      <PeopleOverflowTooltip
        people={omittedValues.map((omitted) => ({
          id: omitted.value as Person["id"],
          name: omitted.label,
        }))}
      >
        <span>
          {intl.formatMessage(
            {
              id: "formFields.personRelationshipField.overflow",
              defaultMessage: "+ {count} more",
              description: "Overflow tag on the multi-person chip area",
            },
            { count: omittedValues.length }
          )}
        </span>
      </PeopleOverflowTooltip>
    ),
    [intl]
  );

  const peopleIdsRules = useMemo<Rule[]>(() => extraRules ?? [], [extraRules]);

  useEffect(() => {
    // Re-sync chips when the upstream associatedPeople list changes, while
    // preserving any user-in-progress selection in multi mode.
    setSelectedPeople((current) => {
      const next = resolveSelectedPeople(multiselect, associatedPeople, current);
      const ids = next.map((person) => person.id);
      if (multiselect) {
        form.setFieldValue("peopleIds", ids);
      } else {
        form.setFieldValue("personId", ids[0]);
      }
      return next;
    });
  }, [associatedPeople, form, multiselect]);

  return (
    <>
      <FormItem
        label={label}
        name={formFieldName}
        required={required}
        rules={peopleIdsRules}
        showLabel={false}
      >
        <SelectField<Person["id"][]>
          allowClear={allowClear}
          className={className}
          label={label}
          locked={disabled}
          maxTagCount={multiselect ? maxTagCount : undefined}
          maxTagPlaceholder={multiselect ? maxTagPlaceholder : undefined}
          mode={multiselect ? "multiple" : undefined}
          onClear={() => handleRemove()}
          onClick={disabled ? undefined : handleOpenModal}
          onDeselect={multiselect ? (value) => handleRemove(value as Person["id"]) : undefined}
          open={false}
          options={options}
          prefixIcon={
            <EntityTypeShapedIcon className={styles.entityIcon} entityType={EntityType.PERSON} />
          }
          required={required}
          showArrow={false}
          tagRender={multiselect ? tagRender : undefined}
        />
      </FormItem>

      {modalVisible && (
        <PeopleAssociation
          allowAdd={allowAdd}
          assignedPeople={selectedPeople}
          fixedCompany={associatedCompany}
          maxCount={multiselect ? MULTI_PERSON_LIMIT : undefined}
          multiselect={multiselect}
          onHide={hideModal}
          onSelect={handleSelect}
          onUpdatePrimaryPerson={multiselect ? handleUpdatePrimaryPerson : undefined}
          primaryPersonId={multiselect ? primaryPersonId : undefined}
          suggestedPeople={suggestedPeople}
          supportsPrimaryEntity={multiselect}
          zIndex={zIndex}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  onFetchPeople: fetchPeople,
  onFetchPerson: fetchPerson,
};

export default connect(null, mapDispatchToProps)(PersonRelationshipField);
